import React, { useEffect, useRef } from "react";
import { Send, MessageCircle, Moon } from "lucide-react";

// Chat composer: textarea + send button + AI disclosure line + daily quota.
// Enter sends; Shift+Enter inserts a newline.
//
// Daily quota: 10 chats per UTC day per user. Once exhausted the textarea
// + send button are disabled and a soft "sleepy block" panel takes their
// place, framed in Mochi's voice (never punitive, never an "error").
export function ChatComposer({ draft, setDraft, sending, onSend, autoFocus, quota }) {
    const inputRef = useRef(null);

    useEffect(() => {
        if (autoFocus) setTimeout(() => inputRef.current?.focus(), 280);
    }, [autoFocus]);

    const handleKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    const exhausted = !!quota?.exhausted;
    const remaining = quota?.remaining;
    const limit = quota?.limit ?? 10;

    // Quota-exhausted state: replace composer with a sleepy block card.
    if (exhausted) {
        return (
            <div className="p-4 border-t border-warm-300/30 bg-surface/40" data-testid="chat-quota-blocked">
                <div className="rounded-2xl bg-cream border border-warm-300/40 px-4 py-3.5 flex items-start gap-3">
                    <Moon size={18} className="text-warm-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                        <p className="font-display text-warm-900 text-sm leading-snug">
                            i&apos;m sleepy 💤 we&apos;ve chatted {limit} times today 🍡
                        </p>
                        <p className="font-body text-[11px] text-warm-500 mt-1">
                            come back tomorrow — resets at midnight UTC.
                        </p>
                    </div>
                </div>
                <p className="text-[10px] font-body text-warm-500 text-center mt-2 inline-flex items-center gap-1 w-full justify-center">
                    <MessageCircle size={10} /> {limit}/{limit} used · daily limit
                </p>
            </div>
        );
    }

    // Soft "running low" pill once the user is within the last 3 messages.
    const lowRemaining = typeof remaining === "number" && remaining <= 3;

    return (
        <div className="p-3 border-t border-warm-300/30 bg-surface/40">
            <div className="flex items-end gap-2 bg-cream rounded-3xl border border-warm-300/40 pl-4 pr-1.5 py-1.5 focus-within:border-terracotta transition-all">
                <textarea
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Say something cozy…"
                    rows={1}
                    className="flex-1 bg-transparent resize-none outline-none font-body text-warm-900 placeholder:text-warm-500 max-h-28 py-2"
                    data-testid="chat-input"
                />
                <button
                    onClick={onSend}
                    disabled={!draft.trim() || sending}
                    className="w-10 h-10 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:opacity-40 text-cream flex items-center justify-center shrink-0 transition-all active:scale-95"
                    data-testid="chat-send"
                    aria-label="Send message"
                >
                    <Send size={16} />
                </button>
            </div>
            <p
                className="text-[10px] font-body text-warm-500 text-center mt-2 inline-flex items-center gap-1 w-full justify-center"
                data-testid="chat-quota-footer"
            >
                <MessageCircle size={10} /> Powered by Claude · responses are AI-generated
                {typeof remaining === "number" && (
                    <span
                        className={`ml-1 ${lowRemaining ? "text-terracotta" : "text-warm-500"}`}
                        data-testid="chat-quota-remaining"
                    >
                        · {remaining}/{limit} left today
                    </span>
                )}
            </p>
        </div>
    );
}
